import React from "react";
import { Currency } from "@/types";

export interface MoneyAmountProps {
  /** The amount to display */
  amount: number | string;
  /** The currency as a string (e.g., "SAR", "AED") or Currency enum */
  currency?: string | Currency;
  /** Number of decimal places to show (default: 2) */
  decimalPlaces?: number;
  /** Whether to show the currency symbol (default: true) */
  showCurrency?: boolean;
  /** Custom CSS classes for the container */
  className?: string;
  /** Custom CSS classes for the amount text */
  amountClassName?: string;
  /** Custom CSS classes for the currency symbol */
  currencyClassName?: string;
  /** Special character to be converted by font (placeholder for now) */
  specialCharacter?: string;
  /** Current language for symbol positioning (default: "en_US") */
  language?: string;
  /** Custom gap between currency symbol and amount (default: "gap-0.5") */
  gapClassName?: string;
}

/**
 * Converts string currency codes to Currency enum
 */
export const stringToCurrency = (currency: string): Currency => {
  const currencyMap: Record<string, Currency> = {
    SAR: Currency.SAR,
    AED: Currency.AED,
    KWD: Currency.KWD,
    QAR: Currency.QAR,
    BHD: Currency.BHD,
    OMR: Currency.OMR,
  };
  return currencyMap[currency.toUpperCase()] ?? Currency.SAR;
};

/**
 * Gets the currency symbol for display
 */
export const getCurrencySymbol = (currency: Currency): string => {
  switch (currency) {
    case Currency.SAR:
      return "à";
    case Currency.AED:
      return "á";
    case Currency.KWD:
      return "د.ك";
    case Currency.QAR:
      return "ر.ق";
    case Currency.BHD:
      return "د.ب";
    case Currency.OMR:
      return "ر.ع";
    default:
      return "";
  }
};

/**
 * Formats a number with proper decimal places and thousands separators
 */
export const formatNumber = (
  amount: number | string,
  decimalPlaces: number = 2,
): string => {
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) {
    return "0.00";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(numericAmount);
};

/**
 * Centralized money amount rendering component
 *
 * Features:
 * - Consistent formatting across the application
 * - Support for both string and enum currency types
 * - Proper number formatting with decimal places
 * - RTL support for Arabic currencies
 * - Customizable styling
 * - Special character support for font conversion
 */
export default function MoneyAmount({
  amount,
  currency = Currency.SAR,
  decimalPlaces = 2,
  showCurrency = true,
  className = "",
  amountClassName = "",
  currencyClassName = "",
  specialCharacter,
  language = "en_US",
  gapClassName = "gap-1",
}: MoneyAmountProps) {
  // Convert string currency to enum if needed
  const currencyEnum =
    typeof currency === "string" ? stringToCurrency(currency) : currency;

  // Format the amount
  const formattedAmount = formatNumber(amount, decimalPlaces);

  // Get currency symbol
  const currencySymbol = getCurrencySymbol(currencyEnum);

  // Determine symbol position based on language
  // English: symbol on the left, Arabic: symbol on the right
  const isArabic = language.startsWith("ar");
  const symbolOnLeft = !isArabic; // English gets symbol on left, Arabic on right

  // Add special character if provided (placeholder for font conversion)
  const displayAmount = specialCharacter
    ? `${specialCharacter} ${formattedAmount}`
    : formattedAmount;

  return (
    <span className={`inline-flex items-center ${gapClassName} ${className}`}>
      {showCurrency && currencySymbol && symbolOnLeft && (
        <span className={`text-inherit ${currencyClassName}`}>
          {currencySymbol}
        </span>
      )}
      <span className={`text-inherit ${amountClassName}`}>{displayAmount}</span>
      {showCurrency && currencySymbol && !symbolOnLeft && (
        <span className={`text-inherit ${currencyClassName}`}>
          {currencySymbol}
        </span>
      )}
    </span>
  );
}

/**
 * Utility function to create a money amount with common defaults
 * This can be used as a drop-in replacement for the existing renderAmount function
 */
export const renderMoneyAmount = (
  currency: Currency | string,
  amount: number | string,
  options?: Partial<MoneyAmountProps>,
): React.ReactNode => {
  return <MoneyAmount amount={amount} currency={currency} {...options} />;
};

/**
 * Hook for getting a formatted amount function (similar to the existing ctx.amount)
 * This maintains backward compatibility while using the new component
 */
export const useMoneyAmount = (
  defaultCurrency: Currency | string = Currency.SAR,
  language: string = "en_US",
) => {
  return React.useCallback(
    (amount: number | string, currency?: Currency | string) => {
      return renderMoneyAmount(currency ?? defaultCurrency, amount, {
        language,
      });
    },
    [defaultCurrency, language],
  );
};
